import '../styles/index.scss';

import { graphql } from 'gatsby';
import { PropTypes } from 'prop-types';
import React from 'react';

import Footer from '../components/Footer';
import Header from '../components/Header';
import HomepageActiveEconomy from '../components/Homepage/HomepageActiveEconomy';
import HomepageBriefings from '../components/Homepage/HomepageBriefings';
import HomepageHero from '../components/Homepage/HomepageHero';
import HomepageHeroLately from '../components/Homepage/HomepageHeroLately';
import HomepageLatestFromCourier from '../components/Homepage/HomepageLatestFromCourier';
import HomepageLibrary from '../components/Homepage/HomepageLibrary';
import HomepageLife from '../components/Homepage/HomepageLife';
import HomepageWorkshop from '../components/Homepage/HomepageWorkshop';
import Metadata from '../components/Metadata/Metadata';
import { COLORS } from '../constants/colors';

const homepageTheme = {
  backgroundColor: COLORS.coral,
  textColor: COLORS.liquorice,
};

const comparator = (a, b) => {
  return new Date(b.publishedDate) - new Date(a.publishedDate);
};

const IndexPage = ({ data }) => {
  const {
    articleListAll,
    articleListWorkshop,
    articleLongFormAll,
    articleLongFormWorkshop,
    articlePodcastAll,
    briefingsDescription,
    contentfulCourierFeedHomepage,
    lifeDescription,
    workshopDescription,
  } = data;

  const {
    activeEconomy,
    briefings,
    hero,
    lately,
    latest,
    life,
    metadata,
    workshop,
  } = contentfulCourierFeedHomepage;

  // todo: add useMemo for these
  const latestAll = [
    ...articleListAll.nodes,
    ...articleLongFormAll.nodes,
    ...articlePodcastAll.nodes,
  ]
    .sort(comparator)
    .slice(0, 10);

  const latestArticlesWorkshop = [
    ...articleListWorkshop.nodes,
    ...articleLongFormWorkshop.nodes,
  ].sort(comparator);

  return (
    <>
      <Metadata data={metadata} homepage />
      <div className="bg-whitish">
        <Header
          backgroundColor={homepageTheme.backgroundColor}
          isHomepage
          showShortLogo
          showTime
          textColor={homepageTheme.textColor}
        />
        <div className="bg-whitish" role="main">
          <HomepageHero
            carousel={hero.carousel}
            featuredArticles={hero.featuredArticles}
            latest={latestAll}
          />
          <HomepageWorkshop
            description={workshopDescription.franchiseDescription}
            featured={workshop.featured}
            latestArticlesWorkshop={latestArticlesWorkshop}
            radar={workshop.radar}
          />
          <HomepageHeroLately
            comments={lately.comments}
            header={lately.header}
            mosaic={lately.mosaic}
            mosaicQuote={lately.mosaicQuote}
            mostRead={lately.mostRead}
          />
          <HomepageLife
            carousel={life.carousel}
            description={lifeDescription.franchiseDescription}
            profiles={life.profiles}
          />
          <HomepageActiveEconomy
            activeEconomy={activeEconomy.activeEconomy}
            header={activeEconomy.header}
            mosaic={activeEconomy.mosaic}
            mosaicQuote={activeEconomy.mosaicQuote}
          />
          <HomepageBriefings
            articles={briefings.articles}
            comments={briefings.comments}
            description={briefingsDescription.franchiseDescription}
          />
          <HomepageLatestFromCourier carousel={latest.carousel} />
          <HomepageLibrary />
        </div>
      </div>
      <Footer />
    </>
  );
};

export const query = graphql`
  query {
    articleListAll: allContentfulArticleList(
      sort: { fields: publishedDate, order: DESC }
      limit: 10
    ) {
      nodes {
        ...ContentfulArticleListBaseFragment
      }
    }
    articleLongFormAll: allContentfulArticleLongForm(
      sort: { fields: publishedDate, order: DESC }
      limit: 10
    ) {
      nodes {
        ...ContentfulArticleLongFormBaseFragment
      }
    }
    articlePodcastAll: allContentfulArticlePodcast(
      sort: { fields: publishedDate, order: DESC }
      limit: 10
    ) {
      nodes {
        ...ContentfulArticlePodcastFragment
      }
    } 
    articleListWorkshop: allContentfulArticleList(
      filter: { franchise: { slug: { eq: "workshop" } } }
      sort: { fields: publishedDate, order: DESC }
      limit: 10
    ) {
      nodes {
        ...ContentfulArticleListBaseFragment
      }
    }
    articleLongFormWorkshop: allContentfulArticleLongForm(
      filter: { franchise: { slug: { eq: "workshop" } } }
      sort: { fields: publishedDate, order: DESC }
      limit: 10
    ) {
      nodes {
        ...ContentfulArticleLongFormBaseFragment
      }
    }
    articleListLife: allContentfulArticleList(
      filter: { franchise: { slug: { eq: "life" } } }
      sort: { fields: publishedDate, order: DESC }
      limit: 10
    ) {
      nodes {
        ...ContentfulArticleListBaseFragment
      }
    }
    articleLongFormLife: allContentfulArticleLongForm(
      filter: { franchise: { slug: { eq: "life" } } }
      sort: { fields: publishedDate, order: DESC }
      limit: 10
    ) {
      nodes {
        ...ContentfulArticleLongFormBaseFragment
      }
    }
    workshopDescription: contentfulFranchises(
      franchiseName: { eq: "Workshop" }
    ) {
      franchiseDescription
    }
    briefingsDescription: contentfulFranchises(
      franchiseName: { eq: "Briefings" }
    ) {
      franchiseDescription
    }
    lifeDescription: contentfulFranchises(franchiseName: { eq: "Life" }) {
      franchiseDescription
    }
    contentfulCourierFeedHomepage {
      metadata {
        ...ContentfulMetadataFragment
      }
      title
      hero {
        carousel {
          ...ContentfulAllContentCards
        }
        featuredArticles {
          ...ContentfulAllContentCards
        }
        featuredPodcasts {
          ...ContentfulAllContentCards
        }
      }
      workshop {
        featured {
          ...ContentfulAllContentCards
        }
        radar {
          id
          title
          url
        }
      }
      lately {
        header {
          ...ContentfulAllContentCards
        }
        mosaic {
          ...ContentfulAllContentCards
        }
        mosaicQuote {
          ...ContentfulCourierFeedCustomCardFragment
        }
        comments {
          ...ContentfulCourierFeedCustomCardFragment
        }
        mostRead {
          ...ContentfulAllContentCards
        }
      }
      life {
        carousel {
          ...ContentfulAllContentCards
        }
        profiles {
          ...ContentfulAllContentCards
        }
      }
      activeEconomy {
        header {
          ...ContentfulAllContentCards
        }
        mosaic {
          ...ContentfulAllContentCards
        }
        mosaicQuote {
          ...ContentfulCourierFeedCustomCardFragment
        }
        activeEconomy {
          ...ContentfulAllContentCards
        }
      }
      briefings {
        articles {
          ...ContentfulAllContentCards
        }
        comments {
          ...ContentfulCourierFeedCustomCardFragment
        }
      }
      latest {
        carousel {
          ...ContentfulAllContentCards
        }
      }
    }
  }
`;

IndexPage.propTypes = {
  data: PropTypes.object,
};

export default IndexPage;
